import React, { useEffect, useState } from "react";
import { api, fmtDay } from "@/lib/api";
import { ChefHat, Utensils, Cookie, Wine, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { downloadReport } from "@/lib/pdf";

const CAT_ICONS = { main: Utensils, dessert: Cookie, drinks: Wine, station: ChefHat };

export default function KitchenPage() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(1);
  const [date, setDate] = useState(""); // specific-day filter (overrides the days window)
  const [dept, setDept] = useState(""); // "" = all departments

  useEffect(() => {
    const params = date ? `date=${date}` : `days=${days}`;
    const q = dept ? `${params}&department=${dept}` : params;
    api.get(`/kitchen/prep?${q}`).then((r) => setData(r.data));
  }, [days, date, dept]);

  const exportPDF = () => {
    if (!data) return;
    downloadReport({
      title: "Kitchen Prep Checklist",
      filename: `Kitchen-Prep-${new Date().toISOString().slice(0, 10)}.pdf`,
      subtitle: date
        ? `Date: ${new Date(`${date}T00:00`).toLocaleDateString("en-MY", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}`
        : `Window: next ${days} day${days > 1 ? "s" : ""}`,
      kpis: [
        { label: "Items", value: data.items?.length || 0 },
        { label: "Live Stations", value: data.stations?.length || 0 },
        { label: "Active Orders", value: data.orders?.length || 0 },
      ],
      sections: [
        {
          title: "Mise en place — aggregated prep",
          columns: ["Item", "Category", "Quantity Needed"],
          rows: (data.items || []).map((it) => [it.name, it.category, it.quantity ? `${it.quantity} ${it.uom || "pax"}` : `${it.pax} pax`]),
          columnStyles: { 2: { halign: "right" } },
        },
        ...(data.stations?.length ? [{
          title: "Live Stations",
          columns: ["Station", "Order", "Event Date"],
          rows: data.stations.map((s) => [s.name, s.order, fmtDay(s.date)]),
        }] : []),
        {
          title: "Active Orders in Window",
          columns: ["Customer", "Event Date", "Pax"],
          rows: (data.orders || []).map((o) => [o.customer_name, fmtDay(o.event_date), String(o.pax)]),
          columnStyles: { 2: { halign: "right" } },
        },
      ],
    });
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="kitchen-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Kitchen Brigade</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Prep Checklist</h1>
          <p className="text-zinc-500 mt-1 text-sm">Auto-generated from upcoming orders.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[1, 3, 7].map((d) => (
            <button key={d} onClick={() => { setDate(""); setDays(d); }} data-testid={`prep-${d}d`}
              className={`px-3 py-2 text-sm rounded-sm border ${!date && days === d ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37]"}`}>
              Next {d} day{d > 1 ? "s" : ""}
            </button>
          ))}
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="prep-date-filter"
            title="Show prep for a specific day"
            className={`px-3 py-2 text-sm rounded-sm border bg-transparent ${date ? "border-[#D4AF37] text-[#D4AF37]" : "border-[#2a2a2a] text-zinc-400 hover:border-[#D4AF37]"}`}
          />
          {date && (
            <button onClick={() => setDate("")} data-testid="prep-date-clear" title="Clear date filter"
              className="px-2 py-2 text-sm rounded-sm border border-[#2a2a2a] text-zinc-400 hover:border-red-500/50 hover:text-red-400">
              ✕
            </button>
          )}
          <Button onClick={exportPDF} data-testid="kitchen-pdf" variant="outline" className="border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm">
            <Download className="w-4 h-4 mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {/* Department filter */}
      <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="dept-filter">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Department</span>
        {[
          { k: "", label: "All" },
          { k: "hot_kitchen", label: "Hot Kitchen" },
          { k: "pastry", label: "Pastry" },
          { k: "cold_kitchen", label: "Cold Kitchen" },
          { k: "beverage", label: "Beverage" },
          { k: "general", label: "General" },
        ].map((d) => (
          <button key={d.k || "all"} onClick={() => setDept(d.k)} data-testid={`dept-${d.k || "all"}`}
            className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${dept === d.k ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#2a2a2a] text-zinc-400 hover:border-[#D4AF37] hover:text-[#D4AF37]"}`}>
            {d.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card-lux p-5 md:col-span-2">
          <div className="label-eyebrow">Aggregate Prep</div>
          <h3 className="font-display text-xl mt-1 mb-4">Mise en place</h3>
          <div className="divide-y divide-[#1a1a1a]">
            {(data?.items || []).map((it, i) => {
              const Icon = CAT_ICONS[it.category] || Utensils;
              return (
                <label key={i} className="flex items-center gap-3 py-3 cursor-pointer hover:bg-[#0c0c0c] -mx-2 px-2 rounded-sm" data-testid={`prep-item-${i}`}>
                  <input type="checkbox" className="accent-[#D4AF37] w-4 h-4" />
                  <Icon className="w-4 h-4 text-[#D4AF37]" />
                  <div className="flex-1">
                    <div className="text-sm">{it.name}</div>
                    <div className="text-xs text-zinc-500 capitalize">{it.category}</div>
                  </div>
                  <div className="font-display text-xl text-[#D4AF37]">{it.quantity ? `${it.quantity} ${it.uom || "pax"}` : `${it.pax} pax`}</div>
                </label>
              );
            })}
            {(!data?.items || data.items.length === 0) && <div className="py-10 text-center text-zinc-600 text-sm">Nothing to prep yet.</div>}
          </div>
        </div>

        <div className="space-y-4">
          <div className="card-lux p-5">
            <div className="label-eyebrow">Live Stations</div>
            <h3 className="font-display text-xl mt-1 mb-3">{data?.stations?.length || 0} to deploy</h3>
            <div className="space-y-2">
              {(data?.stations || []).map((s, i) => (
                <div key={i} className="text-sm p-2 rounded-sm bg-[#0a0a0a] border border-[#1a1a1a]">
                  <div>{s.name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{s.order} · {fmtDay(s.date)}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="card-lux p-5">
            <div className="label-eyebrow">Active Orders</div>
            <h3 className="font-display text-xl mt-1 mb-3">{data?.orders?.length || 0}</h3>
            <div className="space-y-2">
              {(data?.orders || []).map((o) => (
                <div key={o.id} className="text-sm p-2 rounded-sm bg-[#0a0a0a] border border-[#1a1a1a]">
                  <div>{o.customer_name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{fmtDay(o.event_date)} · {o.pax} pax</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
